import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  ArrowLeft, Send, Loader2, User, Phone, Video, MoreVertical,
  Smile, Paperclip, Mic, Check, CheckCheck
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Philosopher data with names and images
const PHILOSOPHERS = {
  sophia: {
    name: "SophIA",
    subtitle: "Professeur de Philosophie",
    image: "https://images.unsplash.com/photo-1602582646830-a54bdf985894?w=200",
    initials: "SO"
  },
  socrate: {
    name: "Socrate",
    subtitle: "Philosophe grec • -470 à -399",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Socrate_du_Louvre.jpg/220px-Socrate_du_Louvre.jpg",
    initials: "SO"
  },
  platon: {
    name: "Platon",
    subtitle: "Philosophe grec • -428 à -348",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Plato-raphael.jpg/220px-Plato-raphael.jpg",
    initials: "PL"
  },
  aristote: {
    name: "Aristote",
    subtitle: "Philosophe grec • -384 à -322",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ae/Aristotle_Altemps_Inv8575.jpg/220px-Aristotle_Altemps_Inv8575.jpg",
    initials: "AR"
  },
  descartes: {
    name: "René Descartes",
    subtitle: "Philosophe français • 1596-1650",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/73/Frans_Hals_-_Portret_van_Ren%C3%A9_Descartes.jpg/220px-Frans_Hals_-_Portret_van_Ren%C3%A9_Descartes.jpg",
    initials: "RD"
  },
  kant: {
    name: "Emmanuel Kant",
    subtitle: "Philosophe allemand • 1724-1804",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f2/Kant_gemaelde_3.jpg/220px-Kant_gemaelde_3.jpg",
    initials: "EK"
  },
  nietzsche: {
    name: "Friedrich Nietzsche",
    subtitle: "Philosophe allemand • 1844-1900",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/Nietzsche187a.jpg/220px-Nietzsche187a.jpg",
    initials: "FN"
  },
  sartre: {
    name: "Jean-Paul Sartre",
    subtitle: "Philosophe français • 1905-1980",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ef/Sartre_1967_crop.jpg/220px-Sartre_1967_crop.jpg",
    initials: "JS"
  },
  pascal: {
    name: "Blaise Pascal",
    subtitle: "Philosophe français • 1623-1662",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/98/Blaise_Pascal_Versailles.JPG/220px-Blaise_Pascal_Versailles.JPG",
    initials: "BP"
  },
  spinoza: {
    name: "Baruch Spinoza",
    subtitle: "Philosophe néerlandais • 1632-1677",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Spinoza.jpg/220px-Spinoza.jpg",
    initials: "BS"
  },
  hegel: {
    name: "Georg W. F. Hegel",
    subtitle: "Philosophe allemand • 1770-1831",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0e/Hegel_portrait_by_Schlesinger_1831.jpg/220px-Hegel_portrait_by_Schlesinger_1831.jpg",
    initials: "GH"
  },
  rousseau: {
    name: "Jean-Jacques Rousseau",
    subtitle: "Philosophe suisse • 1712-1778",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Jean-Jacques_Rousseau_%28painted_portrait%29.jpg/220px-Jean-Jacques_Rousseau_%28painted_portrait%29.jpg",
    initials: "JR"
  },
  saint_augustin: {
    name: "Saint Augustin",
    subtitle: "Philosophe chrétien • 354-430",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ea/Saint_Augustine_by_Philippe_de_Champaigne.jpg/220px-Saint_Augustine_by_Philippe_de_Champaigne.jpg",
    initials: "SA"
  },
  saint_thomas_aquin: {
    name: "Saint Thomas d'Aquin",
    subtitle: "Philosophe chrétien • 1225-1274",
    image: "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e3/St-thomas-aquinas.jpg/220px-St-thomas-aquinas.jpg",
    initials: "TA"
  }
};

const ChatPage = ({ user }) => {
  const navigate = useNavigate();
  const { philosopherId } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(true);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Get philosopher info
  const philosopher = PHILOSOPHERS[philosopherId] || PHILOSOPHERS.sophia;

  useEffect(() => {
    fetchHistory();
    // Simulate online status
    const interval = setInterval(() => {
      setIsOnline(Math.random() > 0.1); // 90% online
    }, 30000);
    return () => clearInterval(interval);
  }, [philosopherId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/private/${user.id}`);
      // Filter messages for this philosopher if needed
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    const tempUserMsg = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: userMessage,
      created_at: new Date().toISOString(),
      status: "sending"
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const response = await axios.post(`${API}/chat/private`, {
        student_id: user.id,
        message: userMessage,
        philosopher_id: philosopherId || "sophia"
      });
      
      // Handle multiple philosopher responses
      const assistantMessages = response.data.assistant_messages || [response.data.assistant_message];
      
      setMessages(prev => {
        const filtered = prev.filter(m => m.id !== tempUserMsg.id);
        return [
          ...filtered, 
          { ...response.data.user_message, status: "delivered" }, 
          ...assistantMessages
        ];
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Erreur d'envoi. Veuillez réessayer.");
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      setInput(userMessage);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('fr-FR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Aujourd'hui";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Hier";
    } else {
      return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
    }
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  const suggestedQuestions = [
    "Expliquez-moi votre philosophie",
    "Quelle est votre œuvre majeure ?",
    "Comment structurer une dissertation ?",
    "Que pensez-vous de la liberté ?"
  ];

  return (
    <div className="h-screen flex flex-col bg-[#E5DDD5]" data-testid="chat-page">
      {/* WhatsApp-style Header */}
      <header className="bg-[#075E54] text-white px-2 py-2 flex items-center gap-2 shadow-md z-10">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate("/dashboard")}
          className="text-white hover:bg-white/10 h-10 w-10"
          data-testid="back-button"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
        
        {/* Philosopher Avatar - Larger on mobile */}
        <Avatar className="w-12 h-12 border-2 border-white/30">
          <AvatarImage src={philosopher.image} alt={philosopher.name} />
          <AvatarFallback className="bg-[#128C7E] text-white text-lg font-bold">
            {philosopher.initials}
          </AvatarFallback>
        </Avatar>
        
        {/* Philosopher Info */}
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-semibold truncate">
            {philosopher.name}
          </h1>
          <p className="text-xs text-white/80 truncate">
            {isOnline ? (
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                En ligne
              </span>
            ) : (
              philosopher.subtitle
            )}
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-10 w-10">
            <Video className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-10 w-10">
            <Phone className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 h-10 w-10">
            <MoreVertical className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Chat Messages - WhatsApp style background */}
      <div 
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-2 py-4"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundColor: '#E5DDD5'
        }}
      >
        {historyLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="bg-white/80 rounded-full p-4">
              <Loader2 className="w-8 h-8 text-[#075E54] animate-spin" />
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full px-4">
            {/* Welcome Card */}
            <div className="bg-[#FCF4CB] rounded-lg p-4 max-w-sm text-center mb-6 shadow-sm">
              <p className="text-sm text-[#54656F]">
                🔒 Les messages envoyés dans cette discussion sont privés et sécurisés.
              </p>
            </div>
            
            {/* Philosopher Card */}
            <div className="bg-white rounded-2xl p-6 max-w-sm text-center shadow-lg">
              <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-[#075E54]">
                <AvatarImage src={philosopher.image} alt={philosopher.name} />
                <AvatarFallback className="bg-[#128C7E] text-white text-2xl font-bold">
                  {philosopher.initials}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-xl font-bold text-[#1A365D] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                {philosopher.name}
              </h2>
              <p className="text-sm text-[#54656F] mb-4">
                {philosopher.subtitle}
              </p>
              <p className="text-sm text-[#54656F] mb-6">
                Posez vos questions sur la philosophie, la méthodologie du Bac, ou mes œuvres.
              </p>
              
              {/* Quick Questions */}
              <div className="space-y-2">
                {suggestedQuestions.map((q, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInput(q)}
                    className="w-full text-left px-4 py-2 bg-[#E7FFDB] rounded-full text-sm text-[#075E54] hover:bg-[#D9FDD3] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-1 max-w-3xl mx-auto">
            {Object.entries(groupedMessages).map(([date, msgs]) => (
              <div key={date}>
                {/* Date separator */}
                <div className="flex justify-center my-4">
                  <span className="bg-white/80 text-[#54656F] text-xs px-3 py-1 rounded-lg shadow-sm">
                    {date}
                  </span>
                </div>
                
                {/* Messages */}
                {msgs.map((message, idx) => {
                  // Get philosopher info for assistant messages
                  const msgPhilosopher = message.role === 'assistant' && message.philosopher_id 
                    ? (PHILOSOPHERS[message.philosopher_id] || { name: message.philosopher_name || philosopher.name, initials: "PH" })
                    : null;
                  
                  return (
                    <div 
                      key={message.id || idx}
                      className={`flex mb-1 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      {/* Philosopher avatar for assistant messages */}
                      {message.role === 'assistant' && msgPhilosopher && (
                        <Avatar className="w-8 h-8 mr-2 mt-1 flex-shrink-0 border border-white shadow-sm">
                          <AvatarImage src={msgPhilosopher.image} alt={msgPhilosopher.name} />
                          <AvatarFallback className="bg-[#128C7E] text-white text-xs">
                            {msgPhilosopher.initials || message.philosopher_name?.substring(0,2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      
                      <div 
                        className={`relative max-w-[80%] px-3 py-2 rounded-lg shadow-sm ${
                          message.role === 'user' 
                            ? 'bg-[#D9FDD3] rounded-tr-none' 
                            : 'bg-white rounded-tl-none'
                        }`}
                      >
                        {/* Philosopher name for assistant messages */}
                        {message.role === 'assistant' && (
                          <p className="text-xs font-bold text-[#075E54] mb-1">
                            {message.philosopher_name || msgPhilosopher?.name || philosopher.name}
                            {message.is_response_to && (
                              <span className="font-normal text-[#667781] ml-1">
                                répond à {message.is_response_to}
                              </span>
                            )}
                          </p>
                        )}
                        
                        {/* Message content */}
                        <p className="text-sm text-[#111B21] whitespace-pre-wrap leading-relaxed">
                          {message.content}
                        </p>
                        
                        {/* Time and status */}
                        <div className={`flex items-center gap-1 justify-end mt-1 ${
                          message.role === 'user' ? 'text-[#667781]' : 'text-[#667781]'
                        }`}>
                          <span className="text-[10px]">
                            {formatTime(message.created_at)}
                          </span>
                          {message.role === 'user' && (
                            message.status === 'sending' ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCheck className="w-4 h-4 text-[#53BDEB]" />
                            )
                          )}
                        </div>
                        
                        {/* Bubble tail */}
                        <div 
                          className={`absolute top-0 w-3 h-3 ${
                            message.role === 'user'
                              ? 'right-[-6px] bg-[#D9FDD3]'
                              : 'left-[-6px] bg-white'
                          }`}
                          style={{
                            clipPath: message.role === 'user'
                              ? 'polygon(0 0, 0% 100%, 100% 0)'
                              : 'polygon(100% 0, 0 0, 100% 100%)'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
            
            {/* Typing indicator */}
            {loading && (
              <div className="flex justify-start mb-1">
                <div className="bg-white rounded-lg rounded-tl-none px-4 py-3 shadow-sm">
                  <p className="text-sm text-[#075E54] italic flex items-center gap-2">
                    <span className="font-semibold">{philosopher.name}</span>
                    <span>est en train d'écrire</span>
                    <span className="flex gap-0.5">
                      <span className="w-1.5 h-1.5 bg-[#075E54] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-[#075E54] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 bg-[#075E54] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* WhatsApp-style Input */}
      <div className="bg-[#F0F2F5] px-2 py-2">
        <form onSubmit={sendMessage} className="flex items-end gap-2">
          <Button 
            type="button"
            variant="ghost" 
            size="icon"
            className="text-[#54656F] hover:bg-white/50 h-10 w-10 flex-shrink-0"
          >
            <Smile className="w-6 h-6" />
          </Button>
          
          <div className="flex-1 relative">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Message"
              className="bg-white rounded-full border-0 pl-4 pr-12 py-6 text-base shadow-sm focus-visible:ring-0"
              disabled={loading}
              data-testid="chat-input"
            />
            <Button 
              type="button"
              variant="ghost" 
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#54656F] hover:bg-transparent h-8 w-8"
            >
              <Paperclip className="w-5 h-5" />
            </Button>
          </div>
          
          <Button 
            type="submit" 
            disabled={!input.trim() || loading}
            size="icon"
            className="bg-[#00A884] hover:bg-[#008C72] rounded-full h-12 w-12 flex-shrink-0 shadow-sm"
            data-testid="send-button"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin text-white" />
            ) : input.trim() ? (
              <Send className="w-5 h-5 text-white" />
            ) : (
              <Mic className="w-5 h-5 text-white" />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ChatPage;
